import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  ["", "— Tür seçin —"],
  ["temel", "Temel Eğitim Sertifikası"],
  ["uzman", "Uzman/Göreve Özgü Sertifikası"],
  ["test_temel", "TMGD Temel Bilinçlendirme Testi"],
  ["test_emniyet", "Emniyet Eğitimi Testi"],
  ["test_gorev", "Göreve Özgü Eğitim Testi"],
  ["diger", "Diğer Eğitim Belgesi"],
];
const MAX_FILES = 40;

export default function BulkCertUpload({ firmId, personnel, onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [trCache, setTrCache] = useState({}); // personnel_id -> trainings[]

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_FILES);
    if (!files.length) return;
    let matches = [];
    try {
      const { data } = await api.post(`/firms/${firmId}/certificate-match`, { filenames: files.map((f) => f.name) });
      matches = data.matches || [];
    } catch { /* eşleştirme yoksa boş öneri */ }
    const next = files.map((f, i) => {
      const m = matches[i] || {};
      return {
        file: f, filename: f.name,
        personnel_id: m.suggestion?.personnel_id || "",
        cert_type: m.cert_type || "",
        training_id: "", publish: true,
      };
    });
    setRows(next);
    // bağlı personellerin eğitimlerini önceden yükle
    const ids = [...new Set(next.map((r) => r.personnel_id).filter(Boolean))];
    ids.forEach((id) => loadTrainings(id));
  };

  const loadTrainings = async (pid) => {
    if (!pid || trCache[pid]) return;
    try { const { data } = await api.get(`/personnel/${pid}/trainings`); setTrCache((c) => ({ ...c, [pid]: data })); }
    catch { setTrCache((c) => ({ ...c, [pid]: [] })); }
  };

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const applyAll = (patch) => setRows((rs) => rs.map((r) => ({ ...r, ...patch })));

  const doUpload = async () => {
    const ready = rows.filter((r) => r.personnel_id);
    if (!ready.length) { toast.error("En az bir dosyaya personel seçin."); return; }
    setBusy(true);
    let ok = 0;
    for (const r of rows) {
      if (!r.personnel_id) continue;
      const fd = new FormData();
      fd.append("file", r.file);
      if (r.cert_type && r.cert_type !== "diger") fd.append("cert_type", r.cert_type);
      if (r.training_id) fd.append("training_id", r.training_id);
      fd.append("section", r.publish ? "customer" : "internal");
      fd.append("publish", String(!!r.publish));
      try { await api.post(`/personnel/${r.personnel_id}/documents`, fd); ok++; } catch { /* atla */ }
    }
    setBusy(false);
    const skipped = rows.length - ready.length;
    toast.success(`${ok}/${rows.length} belge yüklendi.${skipped ? ` ${skipped} belirsiz dosya atlandı.` : ""}`);
    onDone?.();
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-display">Toplu Sertifika Yükle</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Dosya adından personel ve tür önerilir; her satırı kontrol edip düzeltin. Belirsiz isimlerde otomatik kayıt yapılmaz. Birden fazla personelin belgesi tek seferde yüklenebilir.</p>
          <Input type="file" accept=".pdf,.docx,.xlsx" multiple onChange={onPick} data-testid="bulkcert-files" />

          {rows.length > 0 && (
            <>
              <div className="flex flex-wrap items-end gap-2 rounded-md bg-slate-50 p-2">
                <span className="text-xs font-semibold text-slate-600">Tümüne uygula:</span>
                <select className="border rounded px-2 py-1 text-xs" onChange={(e) => { const v = e.target.value; if (v) { applyAll({ personnel_id: v }); loadTrainings(v); } }} data-testid="bulkcert-applyall-person">
                  <option value="">Personel…</option>
                  {personnel.map((p) => <option key={p.id} value={p.id}>{`${p.name} ${p.surname} — ${p.duty}`}</option>)}
                </select>
                <select className="border rounded px-2 py-1 text-xs" onChange={(e) => e.target.value && applyAll({ cert_type: e.target.value })} data-testid="bulkcert-applyall-type">
                  {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{v ? l : "Tür…"}</option>)}
                </select>
                <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" defaultChecked onChange={(e) => applyAll({ publish: e.target.checked })} data-testid="bulkcert-applyall-publish" /> Yayımla</label>
              </div>

              <div className="max-h-80 overflow-y-auto space-y-2">
                {rows.map((r, i) => (
                  <div key={i} className="rounded border border-slate-200 p-2 space-y-1.5" data-testid={`bulkcert-row-${i}`}>
                    <div className="flex items-center gap-1.5 text-sm text-slate-700"><FileText className="h-4 w-4 text-slate-400 shrink-0" /><span className="truncate">{r.filename}</span></div>
                    <div className="grid grid-cols-2 gap-1.5">
                      <div className="space-y-0.5"><Label className="text-[11px]">Personel</Label>
                        <select className={`border rounded px-2 py-1 text-xs w-full ${r.personnel_id ? "" : "border-amber-400"}`} value={r.personnel_id} onChange={(e) => { setRow(i, { personnel_id: e.target.value, training_id: "" }); loadTrainings(e.target.value); }} data-testid={`bulkcert-person-${i}`}>
                          <option value="">Seçin (belirsiz)</option>
                          {personnel.map((p) => <option key={p.id} value={p.id}>{`${p.name} ${p.surname} — ${p.duty}`}</option>)}
                        </select>
                      </div>
                      <div className="space-y-0.5"><Label className="text-[11px]">Belge Türü</Label>
                        <select className="border rounded px-2 py-1 text-xs w-full" value={r.cert_type} onChange={(e) => setRow(i, { cert_type: e.target.value })} data-testid={`bulkcert-type-${i}`}>
                          {DOC_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </div>
                      <div className="space-y-0.5"><Label className="text-[11px]">Eğitim Kaydı (ops.)</Label>
                        <select className="border rounded px-2 py-1 text-xs w-full" value={r.training_id} onChange={(e) => setRow(i, { training_id: e.target.value })} disabled={!r.personnel_id} data-testid={`bulkcert-training-${i}`}>
                          <option value="">Seçilmedi</option>
                          {(trCache[r.personnel_id] || []).map((t) => <option key={t.id} value={t.id}>{t.training_date || "Tarih girilmedi"}</option>)}
                        </select>
                      </div>
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 self-end pb-1"><input type="checkbox" checked={r.publish} onChange={(e) => setRow(i, { publish: e.target.checked })} data-testid={`bulkcert-publish-${i}`} /> Müşteriye yayımla</label>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bulkcert-cancel">Vazgeç</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" onClick={doUpload} disabled={busy || !rows.length} data-testid="bulkcert-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}<Upload className="h-4 w-4 mr-1" /> Kaydet & Yükle</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
